<template>
  <div class="table-section">
    <div class="table-top">
      <b-row>
        <b-col md="4" lg="4" class="my-1">
          <p class="table-filter text-left">
            <BaseInput
              type="text"
              label="Search"
              :inputType="'text'"
              v-model="filter"
              placeholder="Search something..."
              :hasPreIcon="false"
            >
            </BaseInput>
          </p>
        </b-col>
        <b-col md="8" lg="8" class="my-1">
          <p class="table-action text-right">
            <span class="excel-download-btn-hold">
              <button
                type="button"
                class="btn btn-primary btn-semi-rounded mb-2 mr-2"
              >
                <i class="bx bxs-report"></i> &nbsp; &nbsp;
                <download-excel
                  class="excel-download-btn"
                  :data="items"
                  :fields="json_fields"
                  worksheet="My Worksheet"
                  name="filename.xls"
                >
                  Download As Excel
                </download-excel>
              </button>
            </span>
          </p>
        </b-col>
      </b-row>
    </div>

    <b-table
      striped
      hover
      :fields="fields"
      :items="items"
      :busy="isBusy"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      sort-icon-left
      responsive="sm"
      :current-page="currentPage"
      :per-page="perPage"
      :filter="filter"
      :filterIncludedFields="filterOn"
      :sort-direction="sortDirection"
      @filtered="onFiltered"
    >
      <!-- A virtual column -->
      <template v-slot:cell(index)="data">
        {{ data.index + 1 }}
      </template>

      <template v-slot:cell(disputeStatus)="data">
        <app-status-badge
          :status-value="
            data.item.disputeStatus
              ? data.item.disputeStatus === 'CLOSED'
                ? 'RESOLVED'
                : data.item.disputeStatus === 'DECLINED'
                ? 'DECLINED'
                : data.item.disputeStatus
              : 'unknown'
          "
        ></app-status-badge>
      </template>

      <!-- A custom formatted column -->
      <!--      <template v-slot:cell(customerName)="data">-->
      <!--        <p class="name-field customer-name">-->
      <!--          <span class="actual-name"-->
      <!--            >{{-->
      <!--              `${data.item.customerFirstName} ${data.item.customerMiddleName} ${data.item.customerLastName}`-->
      <!--            }}-->
      <!--          </span>-->
      <!--        </p>-->
      <!--      </template>-->

      <template v-slot:cell(action)="data">
        <p class="name-field">
          <AllDisputesTableActionModal
            :current-data="data.item"
          ></AllDisputesTableActionModal>
        </p>
      </template>

      <!-- Optional default data cell scoped slot -->
      <template v-slot:cell()="data">
        <i>{{ data.value }}</i>
      </template>
    </b-table>

    <b-row>
      <b-col sm="5" md="4" class="my-1">
        <b-form-group
          label="Per page"
          label-cols-sm="6"
          label-cols-md="4"
          label-cols-lg="3"
          label-align-sm="right"
          label-size="sm"
          label-for="perPageSelect"
          class="mb-0"
        >
          <b-form-select
            v-model="perPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
          />
        </b-form-group>
      </b-col>

      <b-col sm="7" md="8" class="my-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="fill"
          size="sm"
          class="my-0"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState } from "vuex";
import StoreUtils from "../../../../utils/BaseUtils/StoreUtils";
import AllDisputesTableActionModal from "@/components/Kleyton/Modals/Dispute/AllDisputesTableActionModal";

export default {
  name: "AllDisputesTable",
  components: { AllDisputesTableActionModal },
  data() {
    return {
      sortBy: "date",
      sortDesc: false,
      totalRows: 1,
      currentPage: 1,
      perPage: 5,
      pageOptions: [5, 10, 15],
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      // Note 'isActive' is left out and will not appear in the rendered table
      fields: [
        // A virtual column that doesn't exist in items
        { key: "index", label: "S/N", sortable: true, class: "text-center" },

        // A column that needs custom formatting
        // { key: "disputeId", label: "Dispute Id", sortable: true },
        { key: "disputeSessionId", label: "Session Id", sortable: true },
        { key: "disputeComment", label: "Comment", sortable: true },
        { key: "disputeIssueType", label: "Issue Type", sortable: true },
        {
          key: "disputeRemark",
          label: "Remark",
          sortable: true
        },
        { key: "disputeStatus", label: "Status", sortable: true },
        { key: "disputeTrnType", label: "Trn Type", sortable: true },
        { key: "disputeBearer", label: "Bearer", sortable: true },
        { key: "disputeCreatedAt", label: "Created At", sortable: true },
        { key: "disputeUpdatedAt", label: "Updated At", sortable: true },
        { key: "disputeMaker", label: "Maker", sortable: true },
        { key: "ftAmount", label: "Amount", sortable: true },
        {
          key: "ftDebitAccountNumber",
          label: "Debit Account Number",
          sortable: true
        },
        {
          key: "ftDebitAccountName",
          label: "Debit Account Name",
          sortable: true
        },
        {
          key: "ftCreditAccountNumber",
          label: "Credit Account Number",
          sortable: true
        },
        {
          key: "ftCreditAccountName",
          label: "Credit Account Name",
          sortable: true
        },
        { key: "ftPaymentReference", label: "Payment Ref", sortable: true },
        { key: "action", label: "Action", sortable: false }
      ],

      json_fields: {
        disputeId: "disputeId",
        disputeSessionId: "disputeSessionId",
        disputeBankCode: "disputeBankCode",
        disputeComment: "disputeComment",
        disputeCreatedAt: "disputeCreatedAt",
        disputeIssueType: "disputeIssueType",
        disputeRemark: "disputeRemark",
        disputeStatus: "disputeStatus",
        disputeTrnType: "disputeTrnType",
        disputeBearer: "disputeBearer",
        disputeMaker: "disputeMaker",
        disputeUpdatedAt: "disputeUpdatedAt",
        ftAmount: "ftAmount",
        ftDebitAccountNumber: "ftDebitAccountNumber",
        ftDebitAccountName: "ftDebitAccountName",
        ftCreditAccountNumber: "ftCreditAccountNumber",
        ftCreditAccountName: "ftCreditAccountName",
        ftPaymentReference: "ftPaymentReference",
        ftContractReference: "ftContractReference",
        ftCounterPartySwitchCode: "ftCounterPartySwitchCode",
        ftTransactionType: "ftTransactionType",
        ftCounterPartyService: "ftCounterPartyService",
        ftCounterPartyBankName: "ftCounterPartyBankName",
        ftNarration: "ftNarration",
        ftSource: "ftSource"
      },
      json_meta: [
        [
          {
            key: "charset",
            value: "utf-8"
          }
        ]
      ]
    };
  },
  computed: {
    ...mapState({
      items: state => state.table.tableData,
      isBusy: state => state.loader.tableLoader
    }),
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    }
  },
  async mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length;
    this.filter = " ";
  },
  methods: {
    fetchTableData() {
      StoreUtils.dispatch("dispute/fetchAllDisputes");
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  },
  created() {
    this.fetchTableData();
  }
};
</script>

<style scoped>
/*.VueTables__date-filter {*/
/*  border: 1px solid #ccc;*/
/*  padding: 6px;*/
/*  border-radius: 4px;*/
/*  cursor: pointer;*/
/*}*/
</style>
